import * as React from 'react'
import { composeRefs } from '../utils/compose-refs'

/* -------------------------------------------------------------------------------------------------
 * Slot
 * -----------------------------------------------------------------------------------------------*/

interface SlotProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode
}

const Slot = React.forwardRef<HTMLElement, SlotProps>((props, forwardedRef) => {
  const { children, ...slotProps } = props
  const childrenArray = React.Children.toArray(children)
  const slottable = childrenArray.find(isSlottable)

  if (slottable) {
    // the new element to render is the one passed as a child of `Slottable`
    const newElement = slottable.props.children as React.ReactNode

    const newChildren = childrenArray.map((child) => {
      if (child === slottable) {
        // because the new element will be the one rendered, we are only interested
        // in grabbing its children (`newElement.props.children`)
        if (React.Children.count(newElement) > 1) return React.Children.only(null)
        return React.isValidElement(newElement)
          ? ((newElement as React.ReactElement<{ children?: React.ReactNode }>).props.children as React.ReactNode)
          : null
      } else {
        return child
      }
    })

    return (
      <SlotClone {...slotProps} ref={forwardedRef}>
        {React.isValidElement(newElement) ? React.cloneElement(newElement, undefined, newChildren) : null}
      </SlotClone>
    )
  }

  return (
    <SlotClone {...slotProps} ref={forwardedRef}>
      {children}
    </SlotClone>
  )
})

Slot.displayName = 'Slot'

/* -------------------------------------------------------------------------------------------------
 * SlotClone
 * -----------------------------------------------------------------------------------------------*/

interface SlotCloneProps {
  children: React.ReactNode
}

const SlotClone = React.forwardRef<any, SlotCloneProps>((props, forwardedRef) => {
  const { children, ...slotProps } = props

  if (React.isValidElement(children)) {
    const childElement = children as React.ReactElement<AnyProps>
    const childRef = getElementRef(childElement)
    return React.cloneElement(childElement, {
      ...mergeProps(slotProps, childElement.props),
      ref: forwardedRef ? composeRefs(forwardedRef, childRef) : childRef
    })
  }

  return React.Children.count(children) > 1 ? React.Children.only(null) : null
})

SlotClone.displayName = 'SlotClone'

/* -------------------------------------------------------------------------------------------------
 * Slottable
 * -----------------------------------------------------------------------------------------------*/

const Slottable = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>
}

/* ---------------------------------------------------------------------------------------------- */

type AnyProps = Record<string, any>

function isSlottable(child: React.ReactNode): child is React.ReactElement<any> {
  return React.isValidElement(child) && child.type === Slottable
}

// React 19 moved `ref` into props; earlier versions keep it on the element and warn when reading
// `props.ref`. Read from whichever location the running React version populates without warning.
function getElementRef(element: React.ReactElement): React.Ref<unknown> | undefined {
  // React <=18 in DEV: reading `element.props.ref` triggers a warning getter, so use `element.ref`.
  let getter = Object.getOwnPropertyDescriptor(element.props, 'ref')?.get
  if (getter && 'isReactWarning' in getter && getter.isReactWarning) {
    return (element as { ref?: React.Ref<unknown> }).ref
  }

  // React 19 in DEV: reading `element.ref` triggers a warning getter, so use `element.props.ref`.
  getter = Object.getOwnPropertyDescriptor(element, 'ref')?.get
  if (getter && 'isReactWarning' in getter && getter.isReactWarning) {
    return (element.props as { ref?: React.Ref<unknown> }).ref
  }

  // Production (no warning getters): React 19 populates `props.ref`, older versions populate `element.ref`.
  return (element.props as { ref?: React.Ref<unknown> }).ref ?? (element as { ref?: React.Ref<unknown> }).ref
}

function mergeProps(slotProps: AnyProps, childProps: AnyProps) {
  // all child props should override
  const overrideProps = { ...childProps }

  for (const propName in childProps) {
    const slotPropValue = slotProps[propName]
    const childPropValue = childProps[propName]

    const isHandler = /^on[A-Z]/.test(propName)
    if (isHandler) {
      // if the handler exists on both, we compose them
      if (slotPropValue && childPropValue) {
        overrideProps[propName] = (...args: unknown[]) => {
          childPropValue(...args)
          slotPropValue(...args)
        }
      }
      // but if it exists only on the slot, we use only this one
      else if (slotPropValue) {
        overrideProps[propName] = slotPropValue
      }
    }
    // if it's `style`, we merge them
    else if (propName === 'style') {
      overrideProps[propName] = { ...slotPropValue, ...childPropValue }
    } else if (propName === 'className') {
      overrideProps[propName] = [slotPropValue, childPropValue].filter(Boolean).join(' ')
    }
  }

  return { ...slotProps, ...overrideProps }
}

export { Slot }
export type { SlotProps }
